import { CheckBox, Flex, MarkdownEditor, Text } from 'components'
import { useEffect } from 'react'
import { LeadConsent, RegistrationConsentItem, RegistrationConsentSubItem } from 'types'
import { formTextProps } from '../../utils'
import FormSection from '../FormSection'

interface Props {
  checked?: boolean
  colorScheme?: string
  content: string | RegistrationConsentItem[]
  consents?: LeadConsent[]
  onChange?: () => void
  onConsentsChange?: (consents: LeadConsent[]) => void
  previewMode?: boolean
}

export default function OptInSection({
  checked,
  colorScheme = 'yellow',
  content,
  consents = [],
  onChange,
  onConsentsChange,
  previewMode,
}: Props) {
  const items = Array.isArray(content) ? content : null
  const text = !items && typeof content === 'string' ? content : null

  if (items?.length && onConsentsChange) {
    const byTitle = new Map(consents.map(c => [c.title, c]))
    return (
      <FormSection key="opt-in" previewMode={previewMode}>
        <Text {...formTextProps}>
          Taking care of your information is a responsibility we take seriously.
        </Text>
        <Flex direction="column" gap={6}>
          {items.map((item, index) => {
            const title = itemTitle(item, index)
            const consent = byTitle.get(title) ?? { accepted: null, title }
            return (
              <OptInItem
                key={title}
                colorScheme={colorScheme}
                consent={consent}
                item={item}
                onUpdate={patch =>
                  onConsentsChange([
                    ...consents.filter(c => c.title !== title),
                    { ...consent, ...patch },
                  ])
                }
              />
            )
          })}
        </Flex>
      </FormSection>
    )
  }

  return (
    <FormSection key="opt-in" previewMode={previewMode}>
      <Text {...formTextProps}>
        Taking care of your information is a responsibility we take seriously.
      </Text>
      {text && (
        <Flex borderRadius="6px" bg="rgba(23, 23, 23, 0.04)">
          <MarkdownEditor readOnly value={text} />
        </Flex>
      )}
      <Flex gap={2} align="center">
        <CheckBox checked={!!checked} labelProps={formTextProps} onChange={onChange!}>
          Agree and Opt In
        </CheckBox>
      </Flex>
    </FormSection>
  )
}

function itemTitle(item: RegistrationConsentItem, index: number): string {
  return item.title?.en ?? `Consent ${index + 1}`
}

function subitemLabel(sub: RegistrationConsentSubItem, index: number): string {
  const title = (sub.title?.en ?? '').trim()
  return title || `Sub-item ${index + 1}`
}

function allSubitemLabels(subitems: RegistrationConsentSubItem[]): string[] {
  return subitems.map((sub, i) => subitemLabel(sub, i))
}

function OptInItem({
  colorScheme = 'yellow',
  consent,
  item,
  onUpdate,
}: {
  colorScheme?: string
  consent: LeadConsent
  item: RegistrationConsentItem
  onUpdate: (patch: Partial<LeadConsent>) => void
}) {
  const { accepted, acceptedSubItems = [], title } = consent
  const subitems = item.subitems ?? []
  const hasSubitems = subitems.length > 0
  const allLabels = hasSubitems ? allSubitemLabels(subitems) : []
  const effectiveSubItems =
    accepted && hasSubitems && acceptedSubItems.length === 0 ? allLabels : acceptedSubItems
  const hasNoOption = !!(item.noText?.en ?? item.noText)
  const options = hasNoOption
    ? [
        { label: <MarkdownEditor readOnly value={item.yesText?.en ?? 'Yes'} />, value: 'yes' },
        { label: <MarkdownEditor readOnly value={item.noText?.en ?? 'No'} />, value: 'no' },
      ]
    : [{ label: <MarkdownEditor readOnly value={item.yesText?.en ?? 'Yes'} />, value: 'yes' }]

  useEffect(() => {
    if (accepted && hasSubitems && acceptedSubItems.length === 0) {
      onUpdate({ acceptedSubItems: allSubitemLabels(subitems) })
    }
  }, [accepted, hasSubitems, acceptedSubItems.length])

  return (
    <Flex direction="column" gap={3}>
      <Flex direction="column" gap={2}>
        <CheckBox
          colorScheme={colorScheme}
          checked={accepted === true}
          labelProps={formTextProps}
          onChange={() => {
            onUpdate({
              accepted: !accepted,
              acceptedSubItems: !accepted && hasSubitems ? effectiveSubItems : undefined,
            })
          }}
        >
          {options[0].label}
        </CheckBox>
        {hasSubitems && accepted === true && (
          <Flex pl={6} direction="column" gap={3}>
            {subitems.map((sub, i) => {
              const label = subitemLabel(sub, i)
              const subChecked = effectiveSubItems.includes(label)
              const wouldLeaveNone = subChecked && effectiveSubItems.length <= 1
              return (
                <Flex key={`${title}-sub-${i}`} direction="column" gap={1}>
                  <CheckBox
                    checked={subChecked}
                    disabled={wouldLeaveNone}
                    labelProps={formTextProps}
                    onChange={() => {
                      if (wouldLeaveNone) return

                      onUpdate({
                        acceptedSubItems: subChecked
                          ? effectiveSubItems.filter(l => l !== label)
                          : [...effectiveSubItems, label],
                      })
                    }}
                  >
                    {label ? <Text textStyle="bodySmall">{sub.title?.en}</Text> : undefined}
                    {sub.description?.en && <MarkdownEditor readOnly value={sub.description.en} />}
                  </CheckBox>
                </Flex>
              )
            })}
          </Flex>
        )}
      </Flex>
      {options.length > 1 && (
        <CheckBox
          colorScheme={colorScheme}
          checked={accepted === false}
          labelProps={formTextProps}
          onChange={checked => {
            if (checked) {
              onUpdate({
                accepted: !accepted,
                acceptedSubItems: undefined,
              })
            }
          }}
        >
          {options[1].label}
        </CheckBox>
      )}
    </Flex>
  )
}
